<template>
    <div>
        <fe-dialog
            v-if="viewMissingGuardianModal && !dismissed"
            data-test="missing-guardian-email-modal"
            width="70vw"
            :header="false"
            :footer="false"
            persistent
        >
            <div class="header-container">
                <h2 class="mb-6 mt-8">Missing Guardian Emails</h2>

                <v-btn class="mt-4" icon @click="dismissed = true">
                    <v-icon style="font-size: 16px;">fal fa-times</v-icon>
                </v-btn>
            </div>

            <p>
                The following {{ missingContactsCount }} students either have no guardian email available and/or a
                guardian email address is invalid and cannot be used. You can print their smartFORMS to send home or
                choose to
                <a
                    href="https://educlimber.illuminateed.com/hc/en-us/articles/4403006399771-Upload-Student-Guardians"
                    target="_blank"
                >
                    update email addresses.
                </a>
            </p>

            <fe-grid
                style="height: 56vh; width: 100%;"
                class="flex-grow-1 grid"
                domLayout="normal"
                :columnDefs="missingGuardianColumnDefs"
                :rowData="missingGuardianRowData"
                :showAddRowBtn="false"
                :showToolbarMenu="false"
                :showScrollbars="true"
                :searchBar="false"
            />

            <div class="text-center mt-2">
                <fe-btn usage="primary" @click="printDownloadAllFiles">Print/Download All Files</fe-btn>
            </div>
        </fe-dialog>

        <h3 style="font-size: 18px; margin-bottom: 4px;">Review Students, Guardians, & Forms</h3>

        <notification
            v-if="missingContactsCount > 0 && hasGuardianEmail"
            fontColor="#BF0D00"
            leftColor="#FF8D85"
            messageBgColor="#F8D1CE"
        >
            There are no valid guardian emails available for {{ missingContactsCount }} of the selected student(s).
            <a href="#" style="color: #BF0D00;" @click.prevent="openDialog">View students</a>
        </notification>

        <notification
            v-if="!hasGuardianEmail"
            fontColor="#BF0D00"
            leftColor="#FF8D85"
            messageBgColor="#F8D1CE"
        >
            There are no valid guardian emails available for the selected student(s).
        </notification>

        <div v-if="hasGuardianEmail">
            <div>
                Review the students, guardian contacts, and forms to send below. Email addresses for guardians are from the
                <a target="_blank"
                href="https://educlimber.illuminateed.com/hc/en-us/articles/4403006399771-Upload-Student-Guardians">Student
                    Guardian Upload.</a>
            </div>

            <div style="margin-top: 24px; margin-bottom: 24px;">
                Students: <span class="count" v-text="studentsCount"></span>
                Guardian Contacts: <span class="count" v-text="guardianContactsCount"></span>
                smartFORMS: <span class="count" v-text="smartformsCount"></span>
            </div>

            <fe-grid
                ref="grid"
                :columnDefs="columnDefs"
                :rowData="sendingRowData"
                :showAddRowBtn="false"
                :showToolbar="false"
                @gridReady="setGridLayout"
                class="guardian-review-grid"
            />

            <fe-btn
                data-test="review-guardians-continue-btn"
                style="margin-top: 16px; margin-left: -8px;"
                usage="primary"
                @click="$emit('goToCommunicationSettings')"
            >
                Continue
            </fe-btn>
        </div>
        <div v-else>
            <div>
                To send smartFORMS to students’ guardians, valid emails are required. An eduCLIMBER administrator may need to set up the student guardian import first. Once this is done, forms can be emailed. 
            </div>

            <fe-btn
                data-test="review-guardians-learn-more-btn"
                style="margin-top: 16px; margin-left: -8px;"
                usage="primary"
                @click="openArticle"
            >
                Learn More
            </fe-btn>
        </div>
    </div>
</template>

<script>
import Notification from './notification.vue'
import SmartFormMixin from '../smartFormMixin'

export default {
    name: 'ReviewEmail',

    components: {
        Notification,
    },

    mixins: [SmartFormMixin],

    props: {
        studentsCount: {
            type: Number,
            required: true
        },
        guardianContactsCount: {
            type: Number,
            required: true
        },
        smartformsCount: {
            type: Number,
            required: true
        },
        columnDefs: {
            type: Array,
            required: true
        },
        sendingRowData: {
            type: Array,
            required: true
        },
        missingContactsCount: {
            type: Number,
            required: true
        },
        missingGuardianRowData: {
            type: Array,
            required: true
        },
        hasGuardianEmail: {
            type: Boolean,
            default: true
        },
        missingContactSelections: {
            type: Array,
            required: true
        }
    },

    data() {
        return {
            dismissed: false,
            viewMissingGuardianModal: false,
        }
    },

    computed: {
        missingGuardianColumnDefs() {
            return this.columnDefs.filter(col => col.field !== 'studentFormCount')
        }
    },

    mounted() {
        this.dismissed = false
    },

    methods: {
        printDownloadAllFiles() {
            this.downloadFormZip(this.missingContactSelections, null, false, null)
            this.viewMissingGuardianModal = false
        },

        openDialog() {
            this.dismissed = false
            this.viewMissingGuardianModal = true
        },

        openArticle() {
            window.open('https://educlimber.illuminateed.com/hc/en-us/articles/4403006399771-Upload-Student-Guardians', '_blank')
        },

        setGridLayout(params) {
            setTimeout(function() {
                let rowHeight = 0
                params.api.forEachNode((node) => {
                    rowHeight = node.rowHeight
                })
                let rowCount = params.api.getDisplayedRowCount()
                let gridHeight = rowHeight * rowCount
                let parentHeight = this.$refs.grid.$parent.$parent.$el.offsetHeight
                if(gridHeight < parentHeight) {
                    params.api.setDomLayout('autoHeight')
                } else {
                    params.api.setDomLayout('normal')
                    this.$refs.grid.$el.style.height = (parentHeight - 100) + 'px'
                }
            }.bind(this), 500)
        }
    },
}
</script>

<style lang="scss" scoped>
.count {
    font-size: 20px;
    font-weight: bold;
    margin-right: 20px;
}

.header-container {
    display: flex;
    align-items: center;
    justify-content: space-between;
}

.guardian-review-grid {
    ::v-deep .ag-layout-auto-height .ag-center-cols-clipper {
        min-height: 0 !important;
    }
}
</style>
